import {createContext,createElement,useCallback,useContext,useEffect,useId,useLayoutEffect,useRef,type ReactNode} from 'react';
import {useBlocker} from 'react-router-dom';

type Register=(id:string,message:string|null)=>void;
const Drafts=createContext<Register|null>(null);
/** One router blocker for the whole screen, including nested upload review.
 * Draft registrations contain no private content and disappear on unmount. */
export function PhotoNavigationGuards({children}:{children:ReactNode}){
 const drafts=useRef(new Map<string,string>()),handled=useRef<string>();
 const register=useCallback<Register>((id,message)=>{if(message)drafts.current.set(id,message);else drafts.current.delete(id);},[]);
 const shouldBlock=useCallback(()=>drafts.current.size>0,[]),blocker=useBlocker(shouldBlock);
 useEffect(()=>{
  if(blocker.state!=='blocked'){handled.current=undefined;return;}
  const key=blocker.location.key;if(handled.current===key)return;handled.current=key;
  const messages=[...new Set(drafts.current.values())];
  if(!messages.length||window.confirm(messages.join('\n\n')))blocker.proceed();else blocker.reset();
 },[blocker]);
 useEffect(()=>{const warn=(event:BeforeUnloadEvent)=>{if(drafts.current.size){event.preventDefault();event.returnValue='';}};window.addEventListener('beforeunload',warn);return()=>window.removeEventListener('beforeunload',warn);},[]);
 return createElement(Drafts.Provider,{value:register},children);
}
/** Register unsaved work with the application guard. The returned release is for
 * a completed save/removal immediately before navigating away. Isolated control
 * fixtures may omit the provider; production photo screens must be below it. */
export function usePhotoNavigationGuard(dirty:boolean,message='Leave without saving your changes?'){
 const register=useContext(Drafts),id=useId();
 useLayoutEffect(()=>{register?.(id,dirty?message:null);return()=>register?.(id,null);},[register,id,dirty,message]);
 useEffect(()=>{if(register||!dirty)return;const warn=(event:BeforeUnloadEvent)=>{event.preventDefault();event.returnValue='';};window.addEventListener('beforeunload',warn);return()=>window.removeEventListener('beforeunload',warn);},[register,dirty]);
 return useCallback(()=>register?.(id,null),[register,id]);
}
